import Maybe from 'true-myth/maybe';
import Result from 'true-myth/result';
import { fromResult } from 'true-myth/toolbelt';

import { competenciesSchema, jobProfilesOptionsResponseSchema } from '../../job-profile/job-profile-schema';
import { jobRoleDetailsSchema } from '../../position-management/job-role-details';

import type { ValidatingHttpFetcherWithBaseUrl } from '../../../../shared/source/validating-http-fetcher';
import type { LoaderFunction } from 'react-router';

const fetchJobRoleDescriptionSections = async (
    validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl,
): Promise<Maybe<readonly string[]>> => {
    const result = await validatingCobrainerApiFetcher.get(jobProfilesOptionsResponseSchema, {
        pathname: `/job-profiles/options`,
    });

    if (result.isErr) {
        return Maybe.nothing();
    }

    const data = fromResult(result);

    return data.map((options) => options.structuredTags);
};

export const createJobRolePageLoader = (
    validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl,
): LoaderFunction => {
    return async (loaderArguments) => {
        const jobArchitectureId = Maybe.of(loaderArguments.params['jobArchitectureId']);
        const jobRoleId = Maybe.of(loaderArguments.params['jobRoleId']);
        const mode = Maybe.of(loaderArguments.params['mode']);

        if (jobRoleId.isNothing || jobArchitectureId.isNothing) {
            return Result.err(new Error('No jobArchitectureId/jobRoleId path parameter present'));
        }

        const [jobRole, competencies, structuredSections] = await Promise.all([
            validatingCobrainerApiFetcher.get(jobRoleDetailsSchema, {
                pathname: `/job-architectures/${jobArchitectureId.value}/roles/${jobRoleId.value}`,
            }),
            validatingCobrainerApiFetcher.get(competenciesSchema, {
                pathname: '/skills/competencies',
            }),
            fetchJobRoleDescriptionSections(validatingCobrainerApiFetcher),
        ]);

        const isInEditMode =
            mode.equals(Maybe.of('edit')) && jobRole.map((role) => role.actions?.canEdit).unwrapOr(false);

        return Result.ok({
            competencies: fromResult(competencies),
            isInEditMode,
            jobRole: fromResult(jobRole),
            structuredSections,
        });
    };
};
